import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { ATTACHMENTS, getRackModel } from "@/js/pages/product/rackbuilder/modelPaths"
import type { RackOptions } from "@/js/pages/product/rackbuilder/RackTypes"

// Attachment clips, footplate, weight stack hardware, and roller parts shared across FM and FML variants

// MARK: Weight Stack Hardware

export function buildTopWeightStackHardware(): PartDefinition {
  return {
    mesh  : getRackModel('FM_SHARED', 'weight stack top'),
    slots : {},
  }
}

export function buildWeightStack300Plates(): PartDefinition {
  return {
    mesh  : getRackModel('FM_SHARED', 'weight stack plates 300'),
    slots : {},
  }
}

// Minimal shared context type — both FMContext and TwinContext satisfy this
interface FootplateContext {
  config: Pick<RackOptions, 'lowRowSelection'>
  materials: MaterialInfo
}

export function createTrolleyClip(left: boolean, trolleySelection: string | undefined): PartDefinition {
  const trolleyAttachment: PartDefinition = {
    mesh        : `${ATTACHMENTS}#${trolleySelection}`,
    slots       : {},
    adjustments : left ? [] : [{ type: 'flip', axis: 'Z' }],
  }

  return {
    mesh        : `${ATTACHMENTS}#clip`,
    slots       : { bottom: trolleySelection ? [trolleyAttachment] : [] },
    adjustments : left ? [] : [{ type: 'rotate', axis: 'Y', amount: 180 }],
  }
}

export function createLatBarClip(latBarMaterial: MaterialInfo, latBarSelection: string | undefined): PartDefinition {
  const latBarAttachment: PartDefinition = {
    mesh      : `${ATTACHMENTS}#${latBarSelection}`,
    slots     : {},
    materials : latBarMaterial,
  }

  return {
    mesh  : `${ATTACHMENTS}#clip`,
    slots : { bottom: latBarSelection ? [latBarAttachment] : [] },
  }
}

export function createLowRowClip(lowRowSelection: string | undefined): PartDefinition {
  const lowRowAttachment: PartDefinition = {
    mesh   : `${ATTACHMENTS}#${lowRowSelection}`,
    slots  : {},
    offset : { z: 0.45 },
  }

  return {
    mesh  : `${ATTACHMENTS}#bottom clip`,
    slots : { front: lowRowSelection ? [lowRowAttachment] : [] },
  }
}

export function createFootplate(context: FootplateContext): PartDefinition {
  const { config, materials } = context
  const lowRowClip = createLowRowClip(config.lowRowSelection)

  return {
    mesh  : getRackModel('FM_SHARED', 'footplate'),
    materials,
    slots : { low: config.lowRowSelection ? lowRowClip : [] },
  }
}

// MARK: Roller Parts

// FM rack roller — used by FM, Indy, LRLP, and Rhino variants
export function createFMRollerPart(): PartDefinition {
  return {
    mesh        : getRackModel('RACK_ROLLERS', 'rm rack roller'),
    slots       : {},
    offset      : { y: 1, x: 0, z: 0 },
    adjustments : [{ type: 'rotate', axis: 'Y', amount: 180 }],
  }
}

// FML rack roller — different mesh source and offset from FM roller
export function createFMLRollerPart(): PartDefinition {
  return {
    mesh   : getRackModel('RACK_ROLLERS', 'rml rack roller'),
    slots  : {},
    offset : { y: -1, x: 0, z: 0 },
  }
}
